import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";
import { generateGiftCouponCode } from "../../../../components/GenerateGiftCouponCode";

const PDGiftIt = ({
    product,
    paymentAmount,
    onClose
}) => {

    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const [couponCode, setCouponCode] = useState("");
    const [showForm, setShowForm] = useState(false);

    const [recipientCode, setRecipientCode] = useState("");
    const [recipientName, setRecipientName] = useState("");
    const [recipientCountry, setRecipientCountry] = useState("");
    const [recipientId, setRecipientId] = useState("");

    const [recipientVerified, setRecipientVerified] = useState(false);

    const [giftMessage, setGiftMessage] = useState("All the best!");
    const [giftSuccess, setGiftSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);

    // -------------------------
    // Generate coupon on load
    // -------------------------
    useEffect(() => {
        setCouponCode(generateGiftCouponCode());
    }, []);

    // -------------------------
    // Validate TU code format
    // -------------------------
    const validateTUCode = (code) => {
        return /^TU[A-Z0-9]{12}$/.test(code);
    };

    // -------------------------
    // Verify Recipient
    // -------------------------
    const verifyRecipient = async () => {

        setErrorMsg("");
        setRecipientVerified(false);
        setRecipientName("");
        setRecipientCountry("");

        if (!validateTUCode(recipientCode)) {
            setErrorMsg("Invalid TU Code (must be 14 chars starting with TU)");
            return;
        }

        try {
            const config = {
                url: "/auth/verifyreferralcode",
                method: "post",
                headers: { Authorization: authState.token },
                data: { think5whyscode: recipientCode }
            };

            const res = await fetchData(config);

            if (res?.status) {
                setRecipientName(res.t5wcode.name);
                setRecipientCountry(res.t5wcode.country);
                setRecipientId(res.t5wcode._id)
                setRecipientVerified(true);
            } else {
                setErrorMsg("Recipient not found");
            }

        } catch (err) {
            setErrorMsg("Recipient not found");
        }
    };

    // -------------------------
    // Save Gift
    // -------------------------
    const saveGift = async () => {

        if (!recipientVerified) {
            setErrorMsg("Please verify recipient first");
            return;
        }

        try {
            setLoading(true);
            setErrorMsg("");

            const requestData = {
                gifterId: authState?.user._id,
                gifterCode: authState?.user?.think5whyscode,
                gifterName: authState?.user?.name,
                product: product,
                giftMessage: giftMessage?.slice(0, 50),
                recipientCode: recipientCode,
                recipientName: recipientName,
                recipientId: recipientId,
                couponCode: couponCode,
                giftValue: paymentAmount,
                couponType: "GIFT",
                status: "CREATED",
                remarks: recipientName,
                createdBy: authState.user._id,
                purchaseDate: new Date(),
                createdAt: new Date(),
                updatedAt: new Date(),
                giftedByWallet: true,
                paymentAmount: paymentAmount
            };

            const config = {
                url: "/pdgiftcoupon",
                method: "post",
                headers: { Authorization: authState.token },
                data: requestData
            };

            await fetchData(config);

            setGiftSuccess(true);
            setShowForm(false);

        } catch (err) {
            setErrorMsg("Failed to create gift. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const isGiftingDisabled = !recipientName || !recipientCountry || 
        !authState?.user?.country || recipientCountry !== authState.user.country;

    return (
        <div className="bg-[#2c313b] border border-purple-500/20 rounded-xl p-2">
           {/* <div className="bg-[#0b1220] border border-purple-500/20 rounded-xl p-5 shadow-lg"> */}
                {/* <div className="text-center mb-2">
                    <div className="text-lg lg:text-xl mb-2">🎁</div>
                    <h2 className="text-sm font-semibold text-purple-300">
                        Gift This Product
                    </h2>
                    <p className="text-xs lg:text-sm text-gray-200 mt-1">
                        Send this product as a thoughtful gift to someone you care about
                    </p>
                </div> */}
                <div className="flex flex-col max-w-4xl space-y-2">
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Product Being Gifted
                        </label>
                        <input
                            value={product}
                            disabled
                            className="w-1/2 mt-1 px-2 py-1 bg-gray-900 border border-gray-700 text-white rounded-lg text-xs lg:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Gift Amount (Will be deducted from wallet)
                        </label>
                        <input
                            value={`₹ ${paymentAmount}`}
                            disabled
                            className="w-1/2 mt-1 px-2 py-1 bg-gray-900 border border-gray-700 text-yellow-300 rounded-lg text-xs lg:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Gifted By (You) {authState.user.think5whyscode}
                        </label>
                        <input
                            value={authState.user.name}
                            disabled
                            className="w-1/2 mt-1 px-2 py-1 bg-gray-900 border border-gray-700 text-white rounded-lg text-xs lg:text-sm"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Gift Coupon Code (Share this with recipient)
                        </label>
                        <input
                            value={couponCode}
                            disabled
                            className="w-1/2 mt-1 px-2 py-1 bg-gray-900 border border-purple-500/30 text-yellow-300 tracking-widest rounded-lg text-xs lg:text-sm font-mono"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Recipient Think5Whys ID (TU Code)
                        </label>

                        <input
                            value={recipientCode}
                            onChange={(e) =>
                                setRecipientCode(e.target.value.toUpperCase())
                            }
                            placeholder="Enter TUXXXXXXXXXXXX"
                            className="w-1/2 mt-1 px-2 py-1 bg-gray-900 border border-gray-700 text-white rounded-lg text-xs lg:text-sm tracking-widest"
                        />

                        <button
                            onClick={verifyRecipient}
                            className="w-fit px-4 mt-2 bg-blue-600 hover:bg-blue-700 text-white py-1 rounded-lg text-xs lg:text-sm"
                        >
                            Verify Recipient
                        </button>

                        {/* {recipientVerified && (
                            <p className="text-green-400 text-xs lg:text-sm text-center mt-2">
                                ✅ Recipient Verified: {recipientName}
                            </p>
                        )} */}
                        {recipientVerified && (
                            <div className="mb-4 bg-gradient-to-br from-[#111827] to-[#1f2937] border border-cyan-500/20 rounded-xl px-4 py-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="text-sm font-bold text-cyan-300">
                                        🔐 Recipient Verification
                                    </div>
                                    <div className="bg-green-500/10 border border-green-500/20 text-green-300 text-xs lg:text-sm px-3 py-1 rounded-full font-semibold">
                                        VERIFIED
                                    </div>
                                </div>
                                {/* Recipient Details */}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center bg-gray-800/50 rounded-lg px-3 py-1">
                                        <span className="text-gray-300 text-xs lg:text-sm">
                                            👤 Payee Name
                                        </span>
                                        <span className="font-semibold text-yellow-300 text-xs lg:text-sm">
                                            {recipientName || "Missing"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center bg-gray-800/50 rounded-lg px-3 py-1">
                                        <span className="text-gray-300 text-xs lg:text-sm">
                                            🌎 Payee Country
                                        </span>
                                        <span
                                            className={`text-xs lg:text-sm font-semibold ${
                                                recipientCountry
                                                    ? "text-green-400"
                                                    : "text-red-400"
                                            }`}
                                        >
                                            {recipientCountry || "Missing"}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center bg-gray-800/50 rounded-lg px-3 py-2">
                                        <span className="text-gray-300 text-xs lg:text-sm">
                                            🏠 Your Country
                                        </span>
                                        <span
                                            className={`text-xs lg:text-sm font-semibold ${
                                                authState?.user?.country
                                                    ? "text-green-400"
                                                    : "text-red-400"
                                            }`}
                                        >
                                            {authState?.user?.country || "Missing"}
                                        </span>
                                    </div>
                                </div>
                                {/* Validation Result */}
                                <div className="mt-4 space-y-2">
                                    {!recipientName && (
                                        <div className="bg-red-500/10 border border-red-500/20 text-red-300 rounded-lg px-3 py-1 text-xs lg:text-sm">
                                            ❌ Payee name not found.
                                        </div>
                                    )}
                                    {!recipientCountry && (
                                        <div className="bg-red-500/10 border border-red-500/20 text-red-300 rounded-lg px-3 py-1 text-xs lg:text-sm">
                                            ❌ Payee country not available.
                                        </div>
                                    )}
                                    {!authState?.user?.country && (
                                        <div className="bg-red-500/10 border border-red-500/20 text-red-300 rounded-lg px-3 py-1 text-xs lg:text-sm">
                                            ❌ Your country not available.
                                        </div>
                                    )}
                                    {recipientCountry &&
                                        authState?.user?.country &&
                                        recipientCountry !== authState.user.country && (
                                            <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 rounded-lg px-3 py-1 text-xs lg:text-sm">
                                                ⚠️ Cross-country gifting is not permitted. Sender and recipient must belong to the same country.
                                            </div>
                                        )}
                                    {recipientName &&
                                        recipientCountry &&
                                        authState?.user?.country &&
                                        recipientCountry === authState.user.country && (
                                            <div className="bg-green-500/10 border border-green-500/20 text-green-300 rounded-lg px-3 py-1 text-xs lg:text-sm font-medium">
                                                ✅ Recipient verified successfully. Country validation passed. Gifting is allowed.
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs lg:text-sm text-gray-200">
                            Gift Message (Max 50 characters)
                        </label>

                        <input
                            value={giftMessage}
                            onChange={(e) =>
                                setGiftMessage(e.target.value.slice(0, 50))
                            }
                            placeholder="Write a short heartfelt message..."
                            className="w-full mt-1 px-2 py-1 bg-gray-900 border border-gray-700 text-white rounded-lg text-sm"
                        />
                    </div>
                    {errorMsg && (
                        <div className="bg-red-900/20 border border-red-500/20 px-2 py-1 rounded-xl">
                            <p className="text-red-400 text-xs lg:text-sm text-center">
                                {errorMsg}
                            </p>
                        </div>
                    )}
                    <div className="flex flex-col gap-2 pt-2">

                        {/* <button
                            onClick={() => setShowForm(false)}
                            className="w-fit px-4 bg-gray-700 hover:bg-gray-600 text-white py-1 rounded-lg text-xs lg:text-sm"
                        >
                            Cancel
                        </button> */}
                        <div className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                checked
                                readOnly
                                className="mt-0.5 accent-orange-400"
                            />
                            <p className="text-xs text-orange-300 leading-snug">
                                By choosing <span className="font-semibold">“Gift It Now”</span>, you acknowledge and accept all Terms & Conditions.
                            </p>
                        </div>
                        <div className="flex flex-row gap-2">
                            <button
                                disabled={loading || !recipientVerified || giftSuccess || isGiftingDisabled}
                                onClick={saveGift}
                                className="w-fit px-4 bg-green-600 hover:bg-green-700 text-white py-1 rounded-lg text-xs lg:text-sm"
                            >
                                {loading ? "Processing..." : "Gift It Now"}
                            </button>
                            <button
                                onClick={onClose}
                                className="text-xs lg:text-sm bg-blue-500 text-gray-100 hover:text-white w-fit px-4 py-1"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
                {/* <div className="flex justify-center mt-4">
                    <button
                        disabled={giftSuccess}
                        onClick={() => setShowForm(true)}
                        className="w-fit px-4 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-xs lg:text-sm"
                    >
                        🎁 Start Gifting
                    </button>
                </div> */}
            {/* </div> */}

            {giftSuccess && (
                <div className="mt-2 bg-green-900/20 border border-green-500/30 px-4 py-1 rounded-xl text-center">
                    <p className="text-green-300 text-xs lg:text-sm">
                        🎉 Gift Created Successfully
                    </p>

                    <p className="text-xs lg:text-sm text-gray-100 mt-2">
                        Share this Coupon Code to you Recipient:
                    </p>

                    <p className="text-yellow-300 font-bold tracking-widest">
                        {couponCode}
                    </p>
                </div>
            )}

            {/* <div className="flex justify-center mt-3">
                <button
                    onClick={onClose}
                    className="text-xs lg:text-sm bg-blue-500 text-gray-100 hover:text-white w-fit px-4 py-1"
                >
                    Close
                </button>
            </div> */}
        </div>
    );
};

export default PDGiftIt;